import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import { DamsEmptyPageProps } from "../page-bases";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { donorApi } from "../../services/settings/donors";
import { PageActions, IDonor, IPagination, IQueryPerams } from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { FormField } from "../../utils/formSchema";
import useInputUpdateHelper from "../../hooks/useInputHelper";
import { useFieldHasError } from "../../hooks/useFieldHasError";
import { isFormFilled } from "../../utils/validators";
import ActionModal from "../../components/Modals/ActionModal";
import { usePagination } from "../../hooks/usePagination";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { ExportModal } from "../../components/Modals/ExportModal";
import { appConfig } from "../../config/config";
import { DonorLogsFilter } from "../../common-ui/page-filters/DonorLogsFilter";
import { formatDate } from "../../utils/common";

interface Donor extends FormField<{ name: string }> {
  submitAttempt?: boolean;
}

interface FiltersState {
  search?: string;
  donor_name?: string;
  is_deleted: boolean;
}

const __DEFAULT_VALUE: Donor = {
  name: { isValid: false, value: "", errorMessage: "required" },
};

function DonorsMainPage() {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const [form, updateForm] = useState<Donor>(__DEFAULT_VALUE);
  const handleInputUpdate = useInputUpdateHelper<Donor>(updateForm);
  const fieldHasError = useFieldHasError<Donor>(form);
  const [isProcessing, setProcess] = useState(false);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState
  } = usePagination<IDonor[]>(donorApi.getDonors);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems
  } = useMangeTableItems(dataset);

  // Add query set state for filters
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const tkeys = ["name", "created_at"];
  const columns = ["Donor Name", "Date Added"];

  // Updated populateDataSet function to handle data formatting
  const populateDataSet = (data: IDonor[], pagination?: IPagination) => {
    const formatData = data.map((donor) => ({
      ...donor,
      created_at: formatDate(donor.created_at),
      updated_at: donor.updated_at ? formatDate(donor.updated_at) : 'Not Yet'
    })); 
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  // Updated fetchData function to use query parameters
  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await donorApi.getDonors({...query, is_deleted: false });
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
        // Update current filters state
        setCurrentFilters(prev => ({ ...prev, ...query }));
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page_info = getEmptyPageInfo(pageState);

  async function handleAddRequest() {
    updateForm((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = isFormFilled<Donor>(form);
    if (formValid) {
      setProcess(true);
      const request =
        pageAction?.type === "add"
          ? donorApi.createDonor(form.name.value)
          : donorApi.updateDonor(pageAction?.id ?? "", {
              name: form.name.value,
            });
      const res = await request;
      if (res.success) {
        fetchData(querySet);
      }
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: res.message,
        title: pageAction?.type === "edit" ? "Update Donor" : "New Donor",
        success: res.success,
      });
      openModel("alert");
      updateForm(__DEFAULT_VALUE);
    }
  }

  function addNewItem() {
    updateForm(__DEFAULT_VALUE);
    openModel("add");
    updatePageAction({
      type: "add",
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
        id: item.id,
        name: item.name,
    });
  }

  function updateField(item: TRowItem) {
    updateForm({
      name: {
        value: item.name,
        isValid: true,
      },
    });
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.name,
      type: "edit",
    });
  }

  async function handleDeleteRequest() {
    setProcess(true);
    const res = await donorApi.deleteDonor(pageAction?.id ?? "");
    if (res.success) {
      fetchData(querySet);
    }
    setProcess(false);
    updatePageAction({
        title: res.success ? "Delete Donor" : "Delete Donor Failed",
        message: res.message,
        success: res.success,
    });
    openModel("alert");
  }

  function toggleAlertModal() {
    if (pageAction?.type === "add") {
      openModel("add");
    } else {
      closeModel();
    }
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await donorApi.exportDonors({
        file_format: 'csv',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'donors_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await donorApi.exportDonors({
        file_format: 'pdf',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'donors_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const renderHeaderDropDownMenu = ({
    closeDialog
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData(querySet);
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={donorApi.deleteDonor}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      title={"Donors"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText="Add Donor"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            {/* Add the DonorLogsFilter component */}
            <DonorLogsFilter<IDonor[]>
              ftn={donorApi.getDonors}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />
            
            <DamsTable
              tableTitle="Donors"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              onTableItemEdit={updateField}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
              tableTitleBadge={selectedRowItems.length===0?undefined:selectedRowItems.length + ' selected' }
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Data"
              subtitle="Choose your preferred format to export donors data"
            />

            {/* Add  Modal */}
            <ModalBase
              open={models.showAddModel}
              title={
                  pageAction?.type === "add" ? "Add New Donor" : "Edit Donor"
              }
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.Programmes}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt={
                pageAction?.type === "add" ? "Add Donor" : "Edit Donor"
              }
              onRightBtnPress={handleAddRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            >
              <Stack gap={2}>
                <ModalItemInput
                  label="Donor"
                  placeholder="Enter Donor Name"
                  handleChange={handleInputUpdate("name")}
                  value={form.name.value}
                  error={fieldHasError('name')}
                  errorMessage={form.name.errorMessage}
                />
              </Stack>
            </ModalBase>

            <ModalBase
              open={models.showDeleteModel}
              title={"Delete Donor"}
              subtitle={
                <Box>
                  Once you delete{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {pageAction?.name}
                  </span>
                  , all related information will be lost forever. Are you sure
                  you want to proceed?
                </Box>
              }
              type={"action"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.delete_icon}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt="Delete"
              onRightBtnPress={handleDeleteRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Donors Found",
    message:
      'No Donors have been added yet. Click the "Add New" button to add a new Programme.',
  },

  "empty-search": {
    title: "No Donors Found",
    message:
      "No Donors found in your database. Please add a new Programme to find them in the Donors tab ",
  },
};

export default DonorsMainPage;